import {
  CanvasTexture,
  Color,
  DoubleSide,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three'

/**
 * A `TextMeshPro` label lying in the world, drawn from a 2D canvas.
 *
 * ## Why a canvas and not the shipped atlas
 *
 * The original's world-space labels — `ConveyorLimitText`, `CantMoveIndicator`,
 * `Bus.iceCountText` — are `TextMeshPro`, not `TextMeshProUGUI`, so they hang
 * off a plain `Transform` and carry a `MeshRenderer` (u!23). That class is
 * **not** stripped, which is why their materials survived intact and every
 * number below is read rather than guessed.
 *
 * What did not survive is the font itself, and it is worth being exact about
 * which half is missing:
 *
 *  - `Texture2D/MikadoBlack SDF Atlas.png` (2048²) **is** in the export;
 *  - `TMP_FontAsset` is a `ScriptableObject`, i.e. a MonoBehaviour, so the
 *    glyph table that says which rect in that atlas is which character is gone
 *    (`tests/iceCountLabel.test.ts` freezes that);
 *  - and no `Mikado*.ttf` exists anywhere in the kit.
 *
 * The atlas is rect-packed, not laid out in code-point order — opening it shows
 * accented Latin sitting next to brackets and digits — so the mapping cannot be
 * reconstructed by inspection either.
 *
 * ❓ **The typeface is therefore a substitution**, and a declared one: these
 * labels are set in `Baloo Da 2 ExtraBold`, the heavy rounded face the bundle
 * *does* ship a TTF for and which the HUD already uses. Everything else — the
 * face colour, the outline colour and width, the drop shadow's offset and
 * dilation, the rect the text lives in — comes from the `.mat` and the prefab.
 *
 * ## Headless
 *
 * `scripts/renderPreview.ts` rasterises without a DOM, so `createWorldText`
 * returns `null` there rather than throwing. Callers treat a missing label the
 * way they treat a missing mesh: they skip it.
 */

/** Read straight out of a TMP `.mat`; see each call site for which one. */
export interface WorldTextStyle {
  /** `_FaceColor`. Tinted further by `setColor`, which is TMP's own `color`. */
  face: Color
  /** `_OutlineColor` / `_OutlineWidth` (0..1 of the SDF spread). */
  outline: Color
  outlineWidth: number
  /** `_UnderlayColor`, `_UnderlayOffsetX/Y`, `_UnderlayDilate`, `_UnderlaySoftness`. */
  underlay?: {
    color: Color
    offsetX: number
    offsetY: number
    dilate: number
    softness: number
  }
}

export interface WorldTextOptions {
  /** The `RectTransform`'s `m_SizeDelta`, in world units before any scale. */
  width: number
  height: number
  style: WorldTextStyle
  /**
   * Cap height as a fraction of the rect's height.
   *
   * ❓ `m_fontSize` went with the `TextMeshPro` component. The labels are all
   * auto-sized single lines in a 20 x 5 rect, so the text is fitted to the rect
   * instead — which is what auto-size does anyway.
   */
  fill?: number
}

/** Pixels per world unit in the backing canvas. */
const RESOLUTION = 32

/**
 * ❓ TMP's `_OutlineWidth` and `_UnderlayDilate` are fractions of the SDF's
 * spread, which is `_GradientScale` texels wide in the atlas. With no atlas to
 * measure against, both are turned into stroke widths as a fraction of the cap
 * height; 0.19 then draws roughly the weight the clip shows on the sign.
 */
const OUTLINE_SCALE = 0.3
const UNDERLAY_SCALE = 0.16

/**
 * 📐 The quarter turn that lays a label flat. Every world-space label in the
 * prefabs is `m_LocalRotation (0.7071068, 0, 0, 0.7071068)` — +90° about X in
 * Unity — which sends the text's +Y (its "up") to +Z and TMP's −Z face to +Y.
 * For `IceCountText` that +Z is the `Head` side of the bus. three's plane
 * faces +Z with text running up +Y, and this port mirrors Z (PORTING §1), so
 * the same lay-flat turn here is −90° about X: up → −Z, face → +Y.
 * `tests/iceCountLabel.test.ts` holds both halves against the prefab.
 */
export const LAY_FLAT_ROTATION_X = -Math.PI / 2

export class WorldText {
  readonly object: Mesh<PlaneGeometry, MeshBasicMaterial>

  private readonly canvas: HTMLCanvasElement
  private readonly ctx: CanvasRenderingContext2D
  private readonly texture: CanvasTexture
  private readonly tint = new Color(1, 1, 1)
  private text = ''

  constructor(private readonly options: WorldTextOptions) {
    this.canvas = document.createElement('canvas')
    this.canvas.width = Math.max(2, Math.round(options.width * RESOLUTION))
    this.canvas.height = Math.max(2, Math.round(options.height * RESOLUTION))
    const ctx = this.canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context for a world label')
    this.ctx = ctx

    this.texture = new CanvasTexture(this.canvas)
    this.texture.colorSpace = SRGBColorSpace
    // The label is tiny on screen and never magnified much; mips on a canvas
    // that is redrawn per value would be regenerated every time for nothing.
    this.texture.minFilter = LinearFilter
    this.texture.generateMipmaps = false

    const material = new MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      depthWrite: false,
      side: DoubleSide,
    })
    this.object = new Mesh(new PlaneGeometry(options.width, options.height), material)
    // The scene's labels are all `m_LocalEulerAnglesHint: {x: 90}` — laid flat,
    // face up, for a camera that looks down at the board. three's plane faces +Z
    // with its text running up +Y, so one quarter turn about X lays it flat with
    // the reading direction along world −Z, which is up-screen for these levels'
    // cameras — and, for a label parented to a bus, toward the nose. (A half
    // turn about Z on top of that reads upside down — measured, not assumed: it
    // put "ROAD FULL" on its head over the lot.)
    this.object.rotation.x = LAY_FLAT_ROTATION_X
    this.object.renderOrder = 2
    this.object.visible = false
  }

  /** `TMP_Text.set_text`. */
  setText(value: string): void {
    if (value === this.text) return
    this.text = value
    this.draw()
  }

  /**
   * `TMP_Text.set_color` — the vertex tint over `_FaceColor`, which is what
   * `BusController.AnimateCantMoveIndicator` tweens.
   */
  setColor(color: Color): void {
    if (color.equals(this.tint)) return
    this.tint.copy(color)
    this.draw()
  }

  dispose(): void {
    this.texture.dispose()
    this.object.material.dispose()
    this.object.geometry.dispose()
  }

  private draw(): void {
    const { ctx, canvas } = this
    const { style } = this.options
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    this.texture.needsUpdate = true
    this.object.visible = this.text.length > 0
    if (!this.text) return

    const cap = canvas.height * (this.options.fill ?? 0.62)
    ctx.font = `800 ${cap}px "Baloo Da 2", system-ui, sans-serif`
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.lineJoin = 'round'
    ctx.miterLimit = 2

    const x = canvas.width / 2
    const y = canvas.height / 2

    const under = style.underlay
    if (under) {
      // `_UnderlayOffsetX/Y` are in the same fraction-of-spread units as the
      // outline; +Y in TMP is up, and the canvas's +Y is down.
      ctx.save()
      ctx.strokeStyle = `#${under.color.getHexString()}`
      ctx.fillStyle = `#${under.color.getHexString()}`
      ctx.filter = under.softness > 0 ? `blur(${under.softness * cap * 0.2}px)` : 'none'
      ctx.lineWidth = under.dilate * cap * UNDERLAY_SCALE
      const dx = under.offsetX * cap * UNDERLAY_SCALE
      const dy = -under.offsetY * cap * UNDERLAY_SCALE
      if (ctx.lineWidth > 0) ctx.strokeText(this.text, x + dx, y + dy)
      ctx.fillText(this.text, x + dx, y + dy)
      ctx.restore()
    }

    if (style.outlineWidth > 0) {
      ctx.strokeStyle = `#${style.outline.getHexString()}`
      ctx.lineWidth = style.outlineWidth * cap * OUTLINE_SCALE
      ctx.strokeText(this.text, x, y)
    }

    const face = style.face.clone().multiply(this.tint)
    ctx.fillStyle = `#${face.getHexString()}`
    ctx.fillText(this.text, x, y)
  }
}

/** Null without a DOM — `scripts/renderPreview.ts` has no canvas to draw on. */
export function createWorldText(options: WorldTextOptions): WorldText | null {
  if (typeof document === 'undefined') return null
  return new WorldText(options)
}

/**
 * `Material/MikadoBlack_Outline1.mat` — `Bus.iceCountText`, resolved through the
 * `ConveyorBox` prefabs' `MeshRenderer.m_Materials`.
 */
export const ICE_COUNT_STYLE: WorldTextStyle = {
  face: new Color(1, 1, 1),
  outline: new Color(0, 0, 0),
  outlineWidth: 0.19,
  underlay: {
    color: new Color(0, 0, 0),
    offsetX: 0,
    offsetY: -0.347,
    dilate: 0.718,
    softness: 0,
  },
}

/** `Material/Font_outline&underlay.mat` — `BusController.cantMoveIndicator`. */
export const CANT_MOVE_STYLE: WorldTextStyle = {
  face: new Color(1, 1, 1),
  outline: new Color(0, 0, 0),
  outlineWidth: 0.179,
  underlay: {
    color: new Color(0, 0, 0),
    offsetX: 0,
    offsetY: 0.098,
    dilate: 0.664,
    softness: 0.433,
  },
}
